import { getSocialProofOrEmpty } from '../../data/adventureSocialProof';

interface AdventureSocialProofBadgeProps {
  adventureId: string;
  /**
   * Contagem REAL de jogadores que terminaram a aventura, vinda do backend
   * (playerStates + playthroughHistory). Quando presente, sobrepõe-se ao valor
   * estático do config de social proof.
   */
  completedCount?: number;
  /** 'card' = discreto, dentro do card; 'hero' = com mais contraste para fundo escuro. */
  variant?: 'card' | 'hero';
}

/**
 * Mostra classificação por estrelas e contagem de jogadores que terminaram
 * uma aventura. Só renderiza se existirem dados reais — se o config não tiver
 * `rating` ou `completedCount` para esta aventura, devolve `null` e nada
 * aparece (em vez de números inventados).
 *
 * A contagem de jogadores que terminaram é sempre real: vem do backend via a
 * prop `completedCount`. O config estático só contribui com `rating`/testemunhos.
 */
export default function AdventureSocialProofBadge({
  adventureId,
  completedCount,
  variant = 'card',
}: AdventureSocialProofBadgeProps) {
  const { rating } = getSocialProofOrEmpty(adventureId);

  const hasRating = typeof rating === 'number' && rating > 0;
  const hasCompleted = typeof completedCount === 'number' && completedCount > 0;

  if (!hasRating && !hasCompleted) return null;

  const isHero = variant === 'hero';

  return (
    <div
      className={
        isHero
          ? 'flex flex-wrap items-center justify-center gap-3 text-white/90 text-sm md:text-base'
          : 'flex items-center gap-3 text-xs text-gray-500'
      }
    >
      {hasRating && (
        <span
          className={
            isHero
              ? 'flex items-center gap-1 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm'
              : 'flex items-center gap-1'
          }
        >
          <RatingStars value={rating!} />
          <span className={isHero ? '' : 'font-bold text-dark'}>
            {rating!.toFixed(1)}
          </span>
        </span>
      )}

      {hasCompleted && (
        <span
          className={
            isHero
              ? 'flex items-center gap-1 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm'
              : 'flex items-center gap-1'
          }
        >
          <CheckIcon className={isHero ? 'w-4 h-4' : 'w-3.5 h-3.5 text-emerald-500'} />
          <span>
            {completedCount!.toLocaleString('pt-PT')} {completedCount === 1 ? 'jogador terminou' : 'jogadores terminaram'}
          </span>
        </span>
      )}
    </div>
  );
}

const STAR_PATH =
  'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';

function RatingStars({ value }: { value: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} estrelas`}>
      {[1, 2, 3, 4, 5].map((i) => {
        const fillRatio = Math.min(1, Math.max(0, value - (i - 1)));
        if (fillRatio >= 1) {
          return <Star key={i} fillClass="text-amber-400" />;
        }
        if (fillRatio <= 0) {
          return <Star key={i} fillClass="text-gray-300" />;
        }
        // Partial fill — half star
        return (
          <span key={i} className="relative inline-block w-4 h-4">
            <span className="absolute inset-0 text-gray-300">
              <Star fillClass="text-gray-300" />
            </span>
            <span
              className="absolute inset-0 overflow-hidden text-amber-400"
              style={{ width: `${Math.round(fillRatio * 100)}%` }}
            >
              <Star fillClass="text-amber-400" />
            </span>
          </span>
        );
      })}
    </span>
  );
}

function Star({ fillClass }: { fillClass: string }) {
  return (
    <svg className={`w-4 h-4 ${fillClass}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d={STAR_PATH} />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
